import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';

import {Observable, of} from 'rxjs';

import * as fromAfbGraphqlApi from '../../afb-graphql-api/reducers';
import {select, Store} from '@ngrx/store';
import {catchError, filter, map, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {Logout} from '../../afb-graphql-api/actions/auth.actions';
import {GetOrganizations} from '../../afb-graphql-api/actions/organization.actions';
import {Organization} from '../../afb-graphql-api/models/organization';

@Injectable()
export class OrganizationListGuard implements CanActivate {

  constructor(private afbGraphqlApiStore: Store<fromAfbGraphqlApi.State>,
              private router: Router) {
  }

  getAccessToken(): Observable<string> {
    return this.afbGraphqlApiStore.pipe(select(fromAfbGraphqlApi.accessToken));
  }

  // Returns false if there's error
  waitForOrganizationsToLoad(): Observable<boolean> {
    return this.afbGraphqlApiStore.pipe(
      select(fromAfbGraphqlApi.isGetOrganizationsLoaded),
      filter((isOrganizationLoaded: boolean) => isOrganizationLoaded),
      withLatestFrom(this.afbGraphqlApiStore.select(fromAfbGraphqlApi.getOrganizationsError)),
      map(([isOrganizationLoaded, errorMessage]: [boolean, string]) => errorMessage == null),
      take(1)
    );
  }

  hasOrganization(): Observable<boolean> {
    return this.afbGraphqlApiStore.pipe(
      select(fromAfbGraphqlApi.organizations),
      map((organizations: Organization[]) => {
        if (organizations.length > 0) {
          return true;
        } else {
          this.router.navigate(['/org/new']);
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.afbGraphqlApiStore.dispatch(new Logout());
        return of(false);
      })
    );
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.getAccessToken().pipe(
      switchMap((accessToken: string) => {
        if (accessToken != null) {
          this.afbGraphqlApiStore.dispatch(new GetOrganizations({accessToken}));
          return this.waitForOrganizationsToLoad().pipe(
            switchMap((isSuccessfullyLoaded: boolean) =>  isSuccessfullyLoaded ? this.hasOrganization() : of(false))
          );
        } else {
          return of(false);
        }
      })
    );
  }
}
